import React, { useEffect, useState } from 'react';
import { api } from '@/lib/api';
import { Mail, FileText, TrendingUp, Clock, AlertCircle, ExternalLink } from 'lucide-react';

export default function Dashboard() {
  const [stats, setStats] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    loadStats();
  }, []);

  const loadStats = async () => {
    try {
      const data = await api.getEmailStats();
      setStats(data.stats);
    } catch (error) {
      console.error('Failed to load stats:', error);
    } finally {
      setLoading(false);
    }
  };

  if (loading) {
    return <div>Načítání...</div>;
  }

  const hasNoEmails = stats?.total === 0;

  return (
    <div>
      <h1 className="text-3xl font-bold text-gray-900 mb-8">Dashboard</h1>

      {/* Email Routing Setup Warning */}
      {hasNoEmails && (
        <div className="mb-6 bg-yellow-50 border-l-4 border-yellow-400 p-4 rounded">
          <div className="flex items-start">
            <AlertCircle className="text-yellow-600 mr-3 flex-shrink-0 mt-0.5" size={24} />
            <div className="flex-1">
              <h3 className="text-lg font-semibold text-yellow-800 mb-2">
                Email Routing není nakonfigurován
              </h3>
              <p className="text-yellow-700 mb-3">
                Zatím nepřicházejí žádné emaily. Pro aktivaci příjmu emailů je potřeba jednorázově nastavit Cloudflare Email Routing.
              </p>
              <div className="space-y-2">
                <p className="text-sm text-yellow-700">
                  <strong>Rychlé kroky:</strong>
                </p>
                <ol className="list-decimal list-inside text-sm text-yellow-700 space-y-1 ml-2">
                  <li>Otevřete Cloudflare Dashboard → Email → Email Routing pro doménu <strong>tensorflow.work</strong></li>
                  <li>Klikněte "Get started" a povolte Email Routing</li>
                  <li>Vytvořte routing rule: <code className="bg-yellow-100 px-1 rounded">*@tensorflow.work</code> → Worker: <code className="bg-yellow-100 px-1 rounded">email-worker</code></li>
                  <li>Uložte a otestujte posláním emailu na <code className="bg-yellow-100 px-1 rounded">test@tensorflow.work</code></li>
                </ol>
                <div className="flex items-center space-x-3 mt-4">
                  <a
                    href="https://dash.cloudflare.com"
                    target="_blank"
                    rel="noopener noreferrer"
                    className="inline-flex items-center px-4 py-2 bg-yellow-600 text-white rounded-md hover:bg-yellow-700 text-sm font-medium"
                  >
                    Otevřít Cloudflare Dashboard
                    <ExternalLink size={16} className="ml-2" />
                  </a>
                  <a
                    href="https://github.com/yourusername/tech-newsletter-cc/blob/main/EMAIL-ROUTING-SETUP.md"
                    target="_blank"
                    rel="noopener noreferrer"
                    className="inline-flex items-center px-4 py-2 bg-white text-yellow-700 border border-yellow-300 rounded-md hover:bg-yellow-50 text-sm font-medium"
                  >
                    Detailní návod
                    <ExternalLink size={16} className="ml-2" />
                  </a>
                </div>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Stats Grid */}
      <div className="grid grid-cols-1 md:grid-cols-4 gap-6 mb-8">
        <StatCard
          title="Celkem emailů"
          value={stats?.total || 0}
          icon={<Mail className="text-blue-600" />}
          color="blue"
        />
        <StatCard
          title="Neanalyzované"
          value={stats?.unanalyzed || 0}
          icon={<Clock className="text-yellow-600" />}
          color="yellow"
        />
        <StatCard
          title="Analyzované"
          value={stats?.analyzed || 0}
          icon={<TrendingUp className="text-green-600" />}
          color="green"
        />
        <StatCard
          title="Zpracované"
          value={stats?.processed || 0}
          icon={<FileText className="text-purple-600" />}
          color="purple"
        />
      </div>

      {/* Sources Breakdown */}
      {stats?.bySource && (
        <div className="card">
          <h2 className="text-xl font-semibold mb-4">Rozpad podle zdrojů</h2>
          <div className="space-y-3">
            {Object.entries(stats.bySource).map(([sourceId, count]) => (
              <div key={sourceId} className="flex items-center justify-between">
                <span className="text-gray-700 capitalize">{sourceId}</span>
                <span className="badge badge-info">{count}</span>
              </div>
            ))}
          </div>
        </div>
      )}
    </div>
  );
}

function StatCard({ title, value, icon, color }) {
  return (
    <div className="card">
      <div className="flex items-center justify-between">
        <div>
          <p className="text-sm text-gray-600">{title}</p>
          <p className="text-3xl font-bold text-gray-900 mt-1">{value}</p>
        </div>
        <div className={`p-3 bg-${color}-100 rounded-lg`}>
          {icon}
        </div>
      </div>
    </div>
  );
}
